import { useCallback, useEffect, useRef } from 'react';
import { meetingSessionService } from '../../../../services/meetingSessionService';
import type { ToastData } from '../../../Toast';

export interface UseMeetingSessionToastsParams {
  showToast: (toast: Omit<ToastData, 'id'>) => string;
  removeToast: (id: string) => void;
}

/**
 * Subscribes to the meeting's cross-tab auth-state events and surfaces them as a single toast.
 *
 * Session-state notifications (session expired, logged out, signed in on another tab) are
 * mutually exclusive views of one thing, so at most one is shown at a time: a new state
 * replaces the previous toast, and returning to the joined identity clears it.
 * Uses the caller's toast store, so it still stacks correctly with unrelated toasts.
 */
export function useMeetingSessionToasts({
  showToast,
  removeToast,
}: UseMeetingSessionToastsParams): void {
  const sessionToastIdRef = useRef<string | null>(null);

  const clearSessionToast = useCallback(() => {
    if (sessionToastIdRef.current) {
      removeToast(sessionToastIdRef.current);
      sessionToastIdRef.current = null;
    }
  }, [removeToast]);

  const showSessionToast = useCallback(
    (toast: Omit<ToastData, 'id'>) => {
      clearSessionToast();
      sessionToastIdRef.current = showToast(toast);
    },
    [showToast, clearSessionToast],
  );

  useEffect(() => {
    return meetingSessionService.onAuthExpiredDuringMeeting(() => {
      showSessionToast({
        type: 'warning',
        title: 'Session expired',
        message:
          'Your login session has expired. You will be signed in again after the meeting ends.',
        duration: 86_400_000, // 24 h — effectively permanent, dismiss via close button
      });
    });
  }, [showSessionToast]);

  useEffect(() => {
    return meetingSessionService.onCrossTabLogout(() => {
      showSessionToast({
        type: 'warning',
        title: 'You are logged out',
        message: 'Your session will be terminated once this meeting ends.',
        duration: 86_400_000,
      });
    });
  }, [showSessionToast]);

  useEffect(() => {
    return meetingSessionService.onCrossTabIdentityChange(() => {
      showSessionToast({
        type: 'warning',
        title: 'Signed in on another tab',
        message:
          'Your updated credentials will apply once this meeting ends, or rejoin to use the new session.',
        action: {
          label: 'Rejoin now',
          onClick: () => window.location.reload(),
        },
        duration: 86_400_000,
        dismissible: false,
      });
    });
  }, [showSessionToast]);

  useEffect(() => {
    return meetingSessionService.onCrossTabConsistent(clearSessionToast);
  }, [clearSessionToast]);
}
